/**
 * The 25 `ChangeKind`s, triaged.
 *
 * The ticker's job is to be readable at a glance while it is *still filling* — a player
 * watching Phase A has thirty seconds of change events and no narration yet, and a flat list
 * of twenty-five equally-weighted strings is not a design, it is a log. So each kind gets two
 * classifications, and they answer different questions:
 *
 * - **`family`** — *what part of the game moved.* Six of them, per the live turn's checkbox. It picks
 * the glyph, so the eye triages on shape before it reads a word: a body change and a world
 * change never look alike even when both read as one grey line.
 * - **`weight`** — *how much it matters.* A `death`, a severe injury and a `time_advanced` are
 * explicitly not the same weight, and weight is what drives colour and emphasis.
 *
 * **Family does not get its own colour, deliberately.** Six hues in a ticker that sits under
 * every chapter would out-shout the prose, which is the one thing the feed exists to protect. Shape
 * carries the family; the three-step emphasis ramp (`quiet`/`normal`/`grave`) carries the
 * weight, and it is the only thing in the feed allowed to be red.
 *
 * Order is never touched here. The engine emits changes in the order it applied them and the
 * ticker renders them in that order, live and historical alike — the arrival order *is* the
 * information during Phase A, and a list that regrouped itself as it filled would destroy it.
 */
import type { Change, ChangeKind } from '@/api/generated';
import { readVitalChange } from '@/features/character/vitals';
import type { UiKey } from '@/i18n/en.generated';

export const CHANGE_FAMILIES = [
  'vitals',
  'inventory',
  'body',
  'knowledge',
  'world',
  'time',
] as const;
export type ChangeFamily = (typeof CHANGE_FAMILIES)[number];

/** The glyph vocabulary. Fewer shapes than kinds: siblings within a family share one. */
export const CHANGE_GLYPHS = [
  'cross',
  'bolt',
  'bowl',
  'drop',
  'box',
  'bandage',
  'spark',
  'chevron',
  'note',
  'pin',
  'person',
  'flag',
  'clock',
  'skull',
] as const;
export type ChangeGlyph = (typeof CHANGE_GLYPHS)[number];

/**
 * How loudly a change is drawn. `grave` is the only one that gets colour — losing a limb's
 * worth of health and the sun going down must not arrive looking the same.
 */
export type ChangeWeight = 'quiet' | 'normal' | 'grave';

interface ChangeTrait {
  family: ChangeFamily;
  glyph: ChangeGlyph;
  /** Before the payload is consulted. `weightOf` can promote it, never demote it. */
  weight: ChangeWeight;
}

/**
 * Exhaustive by construction: `Record<ChangeKind, …>` means a kind the API adds is a
 * typecheck error here rather than a change that silently renders as a blank square (D5).
 */
const TRAITS: Record<ChangeKind, ChangeTrait> = {
  health: { family: 'vitals', glyph: 'cross', weight: 'normal' },
  stamina: { family: 'vitals', glyph: 'bolt', weight: 'normal' },
  hunger: { family: 'vitals', glyph: 'bowl', weight: 'normal' },
  thirst: { family: 'vitals', glyph: 'drop', weight: 'normal' },

  item_gained: { family: 'inventory', glyph: 'box', weight: 'normal' },
  item_consumed: { family: 'inventory', glyph: 'box', weight: 'normal' },
  item_dropped: { family: 'inventory', glyph: 'box', weight: 'normal' },
  // A round count or a use ticking down is bookkeeping until the moment it is not, and the
  // player who cares is the one who opens it.
  item_updated: { family: 'inventory', glyph: 'box', weight: 'quiet' },
  // Never quiet. A trade is the one inventory event with another person on the other side of
  // it, and out here it is usually the most consequential thing in the turn.
  traded: { family: 'inventory', glyph: 'box', weight: 'normal' },

  injury_added: { family: 'body', glyph: 'bandage', weight: 'grave' },
  injury_progressed: { family: 'body', glyph: 'bandage', weight: 'grave' },
  injury_treated: { family: 'body', glyph: 'bandage', weight: 'normal' },
  injury_healed: { family: 'body', glyph: 'bandage', weight: 'normal' },
  status_effect_added: { family: 'body', glyph: 'spark', weight: 'normal' },
  status_effect_removed: { family: 'body', glyph: 'spark', weight: 'normal' },
  death: { family: 'body', glyph: 'skull', weight: 'grave' },

  skill_improved: { family: 'knowledge', glyph: 'chevron', weight: 'normal' },
  skill_learned: { family: 'knowledge', glyph: 'chevron', weight: 'normal' },
  fact_recorded: { family: 'knowledge', glyph: 'note', weight: 'quiet' },
  // A rumour arrives quiet — most of them are scenery — but settling one is the story paying
  // out on a lead the player has been carrying, which is news.
  rumour_recorded: { family: 'knowledge', glyph: 'note', weight: 'quiet' },
  rumour_settled: { family: 'knowledge', glyph: 'note', weight: 'normal' },

  moved: { family: 'world', glyph: 'pin', weight: 'normal' },
  location_discovered: { family: 'world', glyph: 'pin', weight: 'normal' },
  location_updated: { family: 'world', glyph: 'pin', weight: 'quiet' },
  npc_introduced: { family: 'world', glyph: 'person', weight: 'normal' },
  npc_updated: { family: 'world', glyph: 'person', weight: 'quiet' },
  // Bookkeeping the player did not ask for: two cards in the codex becoming one.
  npc_merged: { family: 'world', glyph: 'person', weight: 'quiet' },
  faction_updated: { family: 'world', glyph: 'flag', weight: 'normal' },

  // The clock moves every single turn. It is context, not news.
  time_advanced: { family: 'time', glyph: 'clock', weight: 'quiet' },
  // What the turn cost. Also every turn, also context — but it is the number the survival
  // layer runs on, and until it was recorded nobody could see it at all.
  time_spent: { family: 'time', glyph: 'clock', weight: 'quiet' },
};

export function traitsOf(kind: ChangeKind): ChangeTrait {
  return TRAITS[kind];
}

/**
 * How far a vital has to move in one turn before it stops being attrition and starts being an
 * event. Twelve is roughly the difference between "the day cost you something" and "something
 * hit you" at the engine's scales.
 */
const GRAVE_VITAL_DELTA = 12;

/** Where hunger and thirst stop being a warning and start being the cause of death. */
const GRAVE_VITAL_VALUE = 90;

/**
 * The kind's baseline weight, promoted by what actually happened.
 *
 * The payload is consulted rather than trusted: `data` is a free-form dict (`Change.data` is
 * `dict[str, Any]` on the wire), so every read here is defensive and every miss falls back to
 * the table. A severity string the engine has not published yet must render as a normal
 * injury, not throw.
 *
 * **The payload is read through `readVitalChange`, and slice 2 got this wrong.** It looked for
 * `data.delta` on a `health` change and `data.value` on a hunger or thirst one; the engine
 * writes neither of those names on those kinds (`before`/`after` on damage, `delta`/`after` on
 * the passive rise), so no health change could ever promote and no thirst change could ever be
 * read. It passed its tests because the mock invented the same two field names the client
 * guessed — the exact failure the offline harness caught twice before and named: the harness agreeing with the
 * client instead of with the contract. One module now knows the engine's shape and both callers
 * ask it.
 *
 * Direction — up or down — is still deliberately not computed and not drawn. The engine writes
 * it into `summary` already, and a glyph repeating it would say the same thing twice in half
 * the space. The delta is read here only to decide how loud the row is.
 */
export function weightOf(change: Change): ChangeWeight {
  const base = TRAITS[change.kind].weight;
  if (base === 'grave') return base;

  const vital = readVitalChange(change);
  if (vital === null) return base;

  if (vital.name === 'health' && vital.delta !== null && vital.delta <= -GRAVE_VITAL_DELTA) {
    return 'grave';
  }

  // A vital arriving at the top of its warning range is the engine saying the character is
  // about to start dying of it. Hunger and thirst kill at 100 (BE `rules.py`).
  if ((vital.name === 'hunger' || vital.name === 'thirst') && vital.value >= GRAVE_VITAL_VALUE) {
    return 'grave';
  }

  return base;
}

/**
 * What a screen reader hears before the summary. `summary` is already player-facing prose
 * written by the engine, so this only supplies the grouping the glyph gives sighted readers.
 */
/**
 * The key, not the word. Only one caller renders it (the ticker's `sr-only` prefix) and it
 * has a translator; this file is a pure taxonomy and stays that way.
 *
 * `change.summary` beside it needs nothing here: the engine stores a bundle key plus its
 * slots and `quest/api/display.py` joins the sentence in the story's language, so the label
 * and the clause under it are now the same language. It was not always — the summary used to
 * be an English f-string composed in `quest/engine/engine.py`.
 */
export function familyLabelKey(family: ChangeFamily): UiKey {
  return FAMILY_LABEL_KEYS[family];
}

/**
 * The label for one field of `Change.data`, or null to humanize the key.
 *
 * The set is closed — the backend's `_record()` call sites are the whole vocabulary — but
 * this is a lookup rather than an exhaustive `Record` like `TRAITS` above, because
 * `data` is `dict[str, Any]` on the wire and TypeScript has no type to be exhaustive
 * against. So the guarantee is the other one: **a field this has never seen still renders**,
 * as the humanized key, exactly as every field did before these labels existed. A backend
 * that adds a `data` field is a missing translation here, never a blank row.
 */
export function changeFieldKey(field: string): UiKey | null {
  return FIELD_LABEL_KEYS[field] ?? null;
}

/**
 * One value out of a `Change.data` payload, as a string.
 *
 * `data` is `dict[str, Any]` on the wire and the expanded ticker row renders it verbatim —
 * which means this has to answer for every JSON shape rather than the four the engine happens
 * to emit today. An unrenderable value is a blank row in a panel whose entire job is showing
 * the machine-facing truth, so there is no case that returns nothing.
 */
export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length === 0 ? '—' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => formatChangeValue(item)).join(', ');
  return JSON.stringify(value);
}

const FAMILY_LABEL_KEYS: Record<ChangeFamily, UiKey> = {
  vitals: 'ui.change.family.vitals',
  inventory: 'ui.change.family.inventory',
  body: 'ui.change.family.body',
  knowledge: 'ui.change.family.knowledge',
  world: 'ui.change.family.world',
  time: 'ui.change.family.time',
};

/**
 * Every field the engine's `_record()` calls write, in the order a row tends to read.
 *
 * Two of these are deliberately not what the backend calls them. `name` is the snake_case
 * *key* on a location, NPC or faction change and `display_name` is the readable one, which
 * is backwards from how they read as words — so this panel says `key` and `name`, because
 * the expanded row exists to show the machine-facing truth and the reader opened it to see
 * exactly that distinction.
 */
const FIELD_LABEL_KEYS: Record<string, UiKey> = {
  amount: 'ui.change.field.amount',
  before: 'ui.change.field.before',
  after: 'ui.change.field.after',
  delta: 'ui.change.field.delta',
  source: 'ui.change.field.source',
  item: 'ui.change.field.item',
  display_name: 'ui.change.field.display_name',
  name: 'ui.change.field.name',
  quantity: 'ui.change.field.quantity',
  uses: 'ui.change.field.uses',
  uses_remaining: 'ui.change.field.uses_remaining',
  ammo: 'ui.change.field.ammo',
  remaining: 'ui.change.field.remaining',
  description: 'ui.change.field.description',
  destination: 'ui.change.field.destination',
  given: 'ui.change.field.given',
  received: 'ui.change.field.received',
  terms: 'ui.change.field.terms',
  merged: 'ui.change.field.merged',
  merged_display_name: 'ui.change.field.merged_display_name',
  injury_id: 'ui.change.field.injury_id',
  type: 'ui.change.field.type',
  severity: 'ui.change.field.severity',
  new_severity: 'ui.change.field.new_severity',
  location: 'ui.change.field.location',
  skill: 'ui.change.field.skill',
  level: 'ui.change.field.level',
  new_abilities: 'ui.change.field.new_abilities',
  from: 'ui.change.field.from',
  to: 'ui.change.field.to',
  from_display_name: 'ui.change.field.from_display_name',
  to_display_name: 'ui.change.field.to_display_name',
  state: 'ui.change.field.state',
  connections: 'ui.change.field.connections',
  relationship: 'ui.change.field.relationship',
  status: 'ui.change.field.status',
  fact: 'ui.change.field.fact',
  certainty: 'ui.change.field.certainty',
  verdict: 'ui.change.field.verdict',
  day: 'ui.change.field.day',
  time_of_day: 'ui.change.field.time_of_day',
  season: 'ui.change.field.season',
  seconds: 'ui.change.field.seconds',
  hours: 'ui.change.field.hours',
  minutes: 'ui.change.field.minutes',
  cause: 'ui.change.field.cause',
};
